import { atom } from 'jotai';
import type { LoadingState, TutorialState, Theme, AIInsightsState, AuthState, UserPreferences } from '../utils/types';
import { THEMES } from '../utils/constants';
import { storage } from '../utils/storage';

// Get initial theme
const getInitialTheme = (): Theme => {
  if (typeof window === 'undefined') return 'dark';
  
  const savedTheme = storage.getItem<Theme>('theme', 'dark');
  if (Object.values(THEMES).includes(savedTheme)) {
    return savedTheme;
  }
  
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

// Loading state atoms
export const loadingAtom = atom<boolean>(false);
export const errorAtom = atom<Error | null>(null);

// Tutorial state atoms
export const tutorialStateAtom = atom<TutorialState>({
  isOpen: true,
  stepIndex: 0,
});

// Global loading state
export const globalLoadingAtom = atom<LoadingState>({
  loading: false,
  error: null,
});

// Theme atoms
export const themeAtom = atom<Theme>(getInitialTheme());

// Effect to sync theme with storage and document
export const themeEffectAtom = atom(
  null,
  (get, set, theme: Theme) => {
    set(themeAtom, theme);
    if (typeof window !== 'undefined') {
      storage.setItem('theme', theme);
      document.documentElement.classList.remove(...Object.values(THEMES));
      document.documentElement.classList.add(theme);
    }
  }
);

// AI Insights atom
export const aiInsightsAtom = atom<AIInsightsState>({
  insights: null,
  optimizations: null,
  loading: false,
  error: null
});

// Auth atom
export const authAtom = atom<AuthState>({
  isAuthenticated: false,
  user: null,
  loading: false,
  error: null
});

// Preferences atom
export const preferencesAtom = atom<UserPreferences>({
  theme: getInitialTheme(),
  notifications: true,
  tutorialDismissed: false,
  dateFormat: 'MM/DD/YYYY',
  currency: 'USD',
  language: 'en'
});